import { featureFlags } from '@seen/core/featureFlags'
import { useIsLg } from '@seen/ui'
import { isEmpty } from 'lodash-es'
import { type ReactNode, useCallback, useRef } from 'react'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import { InboxDetailLayout } from '#src/components/layouts/InboxDetailLayout.tsx'
import { InboxArchiveAdvanceContext } from '#src/contexts/InboxArchiveAdvanceContext.ts'
import { InboxSiblingsContext } from '#src/contexts/InboxSiblingsContext.ts'
import { KnockFeedClientContext } from '#src/contexts/KnockFeedClientContext.tsx'
import {
  type InboxListItem,
  useInboxDedupedItems,
  useInboxGroupKeyResolver,
} from '#src/hooks/inbox/useInboxDedupedItems.ts'
import { useInboxSidebar } from '#src/hooks/inbox/useInboxSidebar.ts'
import { useLegacyCommentDedupeKeys } from '#src/hooks/inbox/useLegacyCommentDedupeKeys.ts'
import { useDoneNotificationItems } from '#src/hooks/useDoneNotificationItems.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useKnockDoneFeedClient } from '#src/hooks/useKnockDoneFeedClient.ts'
import { InboxDetailPanel } from '#src/pages/inbox/InboxDetailPanel.tsx'
import { InboxItemRow } from '#src/pages/inbox/InboxItemRow.tsx'
import '@knocklabs/react/dist/index.css'
import { InboxNavigation } from '#src/pages/inbox/InboxNavigation.tsx'
import { InboxViewAs } from '#src/pages/inbox/InboxViewAs.tsx'

const INBOX_ROW_HEIGHT = 62

export function InboxDonePage(): ReactNode {
  const doneFeedClient = useKnockDoneFeedClient()
  const {
    items: doneItems,
    loading,
    hasMore,
    isLoadingMore,
    fetchNextPage,
  } = useDoneNotificationItems()

  const legacyDedupeKeys = useLegacyCommentDedupeKeys(doneItems)
  const resolveGroupKey = useInboxGroupKeyResolver({
    legacyDedupeKeys,
    threadGroupingEnabled: useFeatureFlag(featureFlags.inboxTrust),
  })
  const { items: dedupedItems, getSiblings } = useInboxDedupedItems(
    doneItems,
    resolveGroupKey
  )

  const isDesktop = useIsLg()

  const {
    selectItem,
    effectiveSelectedItem,
    effectiveKey,
    sidebarOpen,
    advanceSelectionAwayFrom,
  } = useInboxSidebar({
    items: dedupedItems,
    getSiblings,
    resolveGroupKey,
    feedClient: doneFeedClient,
  })

  const selectionRef = useRef({
    selectItem,
    effectiveKey,
    sidebarOpen,
    isDesktop,
  })
  selectionRef.current = { selectItem, effectiveKey, sidebarOpen, isDesktop }

  const renderItem = useCallback(
    (index: number) => {
      const listItem = dedupedItems[index]
      if (!listItem) {
        return null
      }
      const {
        selectItem: sel,
        effectiveKey: key,
        sidebarOpen: open,
        isDesktop: desktop,
      } = selectionRef.current
      return (
        <DoneListRow
          key={listItem.key}
          listItem={listItem}
          selectItem={desktop ? sel : undefined}
          selectedItemKey={desktop ? key : undefined}
          sidebarOpen={open}
        />
      )
    },
    [dedupedItems]
  )

  if (loading && isEmpty(doneItems)) {
    return null
  }

  const listContent = (
    <div className="flex size-full flex-col">
      <div className="flex shrink-0 justify-between gap-2 border-b p-pagex-default lg:p-pagex-lg">
        <InboxNavigation />
        <InboxViewAs />
      </div>
      {isEmpty(doneItems) && !hasMore ? (
        <div className="flex flex-1 items-center justify-center">
          <span className="text-sm text-muted-foreground">No done items</span>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <VirtualizedList
            renderItem={renderItem}
            itemCount={dedupedItems.length}
            estimateSize={() => INBOX_ROW_HEIGHT}
            scrollRestorationId="inbox-done-page"
            onLoadMore={fetchNextPage}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
          />
        </div>
      )}
    </div>
  )

  if (!isDesktop) {
    return (
      <KnockFeedClientContext.Provider value={doneFeedClient}>
        <InboxSiblingsContext.Provider value={getSiblings}>
          <InboxArchiveAdvanceContext.Provider value={advanceSelectionAwayFrom}>
            {listContent}
          </InboxArchiveAdvanceContext.Provider>
        </InboxSiblingsContext.Provider>
      </KnockFeedClientContext.Provider>
    )
  }

  return (
    <KnockFeedClientContext.Provider value={doneFeedClient}>
      <InboxSiblingsContext.Provider value={getSiblings}>
        <InboxArchiveAdvanceContext.Provider value={advanceSelectionAwayFrom}>
          <InboxDetailLayout
            autoSaveId="inbox-done-detail-layout"
            list={listContent}
            detail={
              effectiveSelectedItem ? (
                <InboxDetailPanel
                  item={effectiveSelectedItem.item}
                  onClose={() => selectItem(undefined)}
                />
              ) : null
            }
          />
        </InboxArchiveAdvanceContext.Provider>
      </InboxSiblingsContext.Provider>
    </KnockFeedClientContext.Provider>
  )
}

function DoneListRow({
  listItem,
  selectItem,
  selectedItemKey,
  sidebarOpen,
}: {
  listItem: InboxListItem
  selectItem?: (key: string | undefined) => void
  selectedItemKey?: string | null
  sidebarOpen?: boolean
}): ReactNode {
  return (
    <InboxItemRow
      item={listItem.item.data}
      notificationItem={listItem.item}
      onSelect={selectItem ? () => selectItem(listItem.key) : undefined}
      isSelected={selectedItemKey === listItem.key}
      sidebarOpen={sidebarOpen}
    />
  )
}
